<!--
  - Copyright (c) 2024-2025. Haodong JU
  -->

<template>
  <v-card
    v-if="show"
    :color="color"
    class="pa-4"
    elevation="0"
  >
    <page-sub-header class="mb-2">
      <slot />
      <template #subtitle>
        <slot name="subtitle" />
      </template>
      <template #action>
        <v-btn
          v-if="isDialog"
          class="mr-2"
          elevation="0"
          @click="closeDialog"
        >
          <v-icon left>
            mdi-close
          </v-icon>
          {{ $t('closed') }}
        </v-btn>
        <v-btn
          v-if="needJumpBtn"
          :disabled="!formValid || !canEdit"
          class="mr-2"
          color="blue lighten-4 black--text"
          elevation="0"
          @click="submitAndJump"
        >
          <v-icon left>
            mdi-content-save
          </v-icon>
          {{ $t('saveAndJump') }}
        </v-btn>
        <v-btn
          v-if="!hideSaveBtn"
          :disabled="!formValid || !canEdit"
          color="amber lighten-4 black--text"
          elevation="0"
          @click="submit"
        >
          <v-icon left>
            mdi-content-save
          </v-icon>
          {{ $t(saveBtnName) }}
        </v-btn>
      </template>
    </page-sub-header>
    <loading-provider :loading="loading">
      <v-form
        v-if="!reloading"
        ref="form"
        v-model="formValid"
        lazy-validation
      >
        <!-- Vertical layout with categories at top -->
        <div class="form-container">
          <!-- Top row: Tag selection -->
          <div
            v-if="tags.length >= 2"
            class="tag-row"
          >
            <div class="tag-items-container">
              <div
                v-for="tag in tags"
                :key="tag"
                :class="['tag-item', selectedTag === tag ? 'tag-item-selected' : '']"
                @click="selectedTag = tag === selectedTag ? null : tag"
              >
                <span>{{ getTagDisplayName(tag) }}</span>
              </div>
            </div>
          </div>

          <!-- Form fields -->
          <div class="form-fields-column">
            <div
              v-for="(f) in visibleFields"
              :key="f.key"
            >
              <div
                :class="{'d-flex justify-content': !f.fullWidth, 'full-width-field': f.fullWidth}"
              >
                <template v-if="!f.fullWidth">
                  <div
                    class="text-body-1 mr-2"
                    style="width: 50%;"
                  >
                    <div class="font-weight-bold">
                      {{ $t(f.name) }}<span
                        v-if="f.required"
                        class="required-indicator"
                      >*</span>
                    </div>
                    <div class="text-body-2">
                      {{ $t(f.hint) }}
                    </div>
                  </div>
                  <div style="width: 10%" />
                  <div
                    style="width: 40%;"
                  >
                    <component
                      :is="f.component"
                      :value="getNestedValue(holderObject, f.key)"
                      @input="val => setNestedValue(holderObject, f.key, val)"
                      dense
                      v-bind="f.componentProps"
                      @change="f.onChanged(holderObject)"
                    />
                    <v-icon
                      v-if="f.componentProps?.iconAppend"
                      class="ml-1 mb-6"
                      @click="f.componentProps?.iconAppend.onClick()"
                    >
                      {{ f.componentProps.iconAppend.icon }}
                    </v-icon>
                  </div>
                </template>
                <template v-else>
                  <div class="mb-2">
                    <div class="font-weight-bold">
                      {{ $t(f.name) }}<span
                        v-if="f.required"
                        class="required-indicator"
                      >*</span>
                    </div>
                    <div class="text-body-2">
                      {{ $t(f.hint) }}
                    </div>
                  </div>
                  <div class="full-width-component">
                    <component
                      :is="f.component"
                      :value="getNestedValue(holderObject, f.key)"
                      @input="val => setNestedValue(holderObject, f.key, val)"
                      dense
                      v-bind="f.componentProps"
                      @change="f.onChanged(holderObject)"
                    />
                    <v-icon
                      v-if="f.componentProps?.iconAppend"
                      class="ml-1 mb-6"
                      @click="f.componentProps?.iconAppend.onClick()"
                    >
                      {{ f.componentProps.iconAppend.icon }}
                    </v-icon>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </v-form>
    </loading-provider>
  </v-card>
</template>

<script>

import { mapSchemaToField } from '@/model/baseForm/BaseForm/baseFormUtils'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import IKUtils from 'innerken-js-utils'

export default {
  name: 'BaseForm',
  components: { LoadingProvider, PageSubHeader },
  props: {
    color: {
      type: String,
      default: '#fefefe'
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    isDialog: {
      type: Boolean,
      default: true
    },
    schema: {
      type: Array,
      default: () => []
    },
    editingObject: {
      type: Object,
      required: false,
      default: () => null
    },
    loading: {
      type: Boolean,
      default: false
    },
    show: {
      type: Boolean,
      default: true
    },
    modelValue: {
      type: Object,
      default: () => ({})
    },
    needJumpBtn: {
      type: Boolean,
      default: false
    },
    saveBtnName: {
      type: String,
      default: 'save'
    },
    hideSaveBtn: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      reloading: false,
      formValid: null,
      holderObject: this.modelValue,
      selectedTag: null
    }
  },
  methods: {
    /**
     * Gets the display name for a tag
     *
     * @param {string} tag - The tag name
     * @returns {string} - The display name for the tag
     */
    getTagDisplayName (tag) {
      // Only handle required and optional tags
      if (tag === 'required') {
        return this.$t('Required Fields')
      } else if (tag === 'optional') {
        return this.$t('Optional Fields')
      }
      return tag
    },

    /**
     * Evaluates whether a field should be visible based on its preconditions and shouldShow function
     *
     * @param {Object} field - The field configuration
     * @param {Object} model - The form data model
     * @returns {boolean} - Whether the field should be visible
     */
    evaluateFieldVisibility (field, model) {
      // If the field has preconditions, evaluate them
      if (field.preconditions) {
        // Check each precondition
        for (const key in field.preconditions) {
          const condition = field.preconditions[key]
          const value = this.getNestedValue(model, key)

          // If condition is a function, call it with the value
          if (typeof condition === 'function') {
            if (!condition(value, model)) {
              return false
            }
          } else if (value !== condition) {
            return false
          }
        }
        return true
      }
      // Fall back to the original shouldShow function
      return field.shouldShow(model)
    },

    closeDialog () {
      this.$emit('close')
      this.reset()
    },
    submit () {
      if (this.$refs.form.validate()) {
        this.$emit('submit', this.holderObject)
      }
    },
    submitAndJump () {
      if (this.$refs.form.validate()) {
        this.$emit('submitAndJump', this.holderObject)
      }
    },
    reset () {
      this.reloading = true
      // Reset selected tag
      this.selectedTag = null

      if (this.editingObject) {
        this.holderObject = IKUtils.deepCopy(this.editingObject)
      } else {
        // Initialize the holder object with default values
        this.holderObject = {}
        this.schema.forEach(item => {
          this.setNestedValue(this.holderObject, item.key, item.default)
        })
      }
      this.reloading = false
    },
    // Helper function to get a nested property value using dot notation
    getNestedValue (obj, path) {
      if (!path || !obj) return undefined
      const keys = path.split('.')
      return keys.reduce((o, k) => (o && o[k] !== undefined) ? o[k] : undefined, obj)
    },
    // Helper function to set a nested property value using dot notation
    setNestedValue (obj, path, value) {
      if (!obj || !path) return
      const keys = path.split('.')
      const lastKey = keys.pop()
      // 先逐层“声明”中间对象
      const parent = keys.reduce((current, key) => {
        // 如果没有这个属性，就用 $set 新增
        if (!(key in current)) {
          this.$set(current, key, {})
        }
        return current[key]
      }, obj)
      // 最后一层直接用 $set，替代 direct assignment
      this.$set(parent, lastKey, value)
    }
  },
  watch: {
    holderObject: {
      deep: true,
      immediate: true,
      handler (newVal) {
        this.$emit('holder-change', newVal)
      }
    },
    schema: {
      deep: true,
      immediate: true,
      handler (val) {
        this.reset()
      }
    },
    show (val) {
      if (val) {
        this.reset()
      }
    },
    editingObject: {
      deep: true,
      immediate: true,
      handler (val) {
        this.$nextTick(() => {
          this.reset()
        })
      }
    },
    modelValue (val) {
      this.holderObject = val
    }
  },
  computed: {
    fields () {
      return this.schema.map(mapSchemaToField)
    },
    // Get all unique tags from the fields (only 'required' and 'optional')
    tags () {
      const result = []
      // Only include 'required' tag if there are required fields
      if (this.fields.some(f => f.tag === 'required')) {
        result.push('required')
      }
      // Only include 'optional' tag if there are optional fields
      if (this.fields.some(f => f.tag === 'optional')) {
        result.push('optional')
      }
      return result
    },
    visibleFields () {
      const res = this.fields.filter(f => (this.evaluateFieldVisibility(f, this.holderObject)) &&
          ((!this.selectedTag || this.tags.length === 0) || f.tag === this.selectedTag))
      console.log(res, 'fields')
      return res
    }
  }
}
</script>

<style scoped>
.full-width-field {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.full-width-component {
  width: 100%;
}

/* Vertical layout styles with categories at top */
.form-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tag-row {
  width: 100%;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding: 0 0 16px 0;
  margin-bottom: 16px;
}

.tag-items-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.form-fields-column {
  width: 100%;
}

.tag-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #f5f5f5;
  border-radius: 4px;
  border-bottom: 2px solid transparent;
  margin-right: 12px;
  font-size: 0.9rem;
  opacity: 0.75;
  position: relative;
}

.tag-item:hover {
  opacity: 1;
  background-color: #e0e0e0;
  transform: none;
  box-shadow: none;
}

.tag-item-selected {
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 500;
  border-bottom: 2px solid #1976d2;
  box-shadow: none;
  opacity: 1;
  transition: all 0.3s ease;
}

.required-indicator {
  color: red;
}
</style>
